import {Alert, Button, Loading, NoResultsDiv, ReactTable} from "fearless-common-ui";
import {ColumnDef, Row as ReactTableRow} from "@tanstack/react-table";
import {User} from "fearless-common";
import CellRender from "../layout/table/CellRender";
import React, {useCallback, useMemo} from "react";
import {useField} from "formik";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import useGet from "../hooks/useGet";
import {backendEndpoints} from "../../libs/backendEndpoints";

const columns: ColumnDef<User>[] = [
  { id: 'id', accessorKey: 'id', header: 'ID', enableSorting: true },
  { id: 'name', accessorKey: 'first_name', header: 'Name', cell: CellRender.FullName, enableSorting: false, },
  { id: 'email', accessorKey: 'email', header: 'Email', enableSorting: false },
];

const headerHeight = '45px';

export default function SelectManyUsers({ name } : { name: string }) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [userObjects, setUserObjects] = React.useState<User[]>([]);
  const { data, error, isLoading } = useGet<User[]>({
    endpoint: backendEndpoints.getAdminUsers,
    params: {
      query: searchQuery,
    }
  })
  const hashMap = userObjects.reduce(
    (acc, value) => {
      acc[value.id] = value;
      return acc;
    },
    {} as Record<number, User>
  );
  const [{ value: selectedUserIds },,{ setValue: setUserIds }] = useField(name);

  const filteredData = useMemo(() => {
    return (data || []).filter(v => !selectedUserIds.includes(v.id.toString()));
  }, [data, selectedUserIds.length]);

  const getLeftActions = useCallback(
    function computeLeftOptions({ rows }: { rows: ReactTableRow<User>[] }) {
      const newUsersHashMap = {...hashMap};
      return  [
        {
          label: 'select as recipient',
          onClick: () => {
            rows.forEach((row) => {
              newUsersHashMap[row.original.id] = row.original;
            });
            setUserObjects(Object.values(newUsersHashMap));
            setUserIds(Object.keys(newUsersHashMap));
          },
        },
      ]
    },
    [hashMap, setUserObjects, setUserIds]
  );
  const getRightActions = useCallback(
    function computeRightOptions({ rows }: { rows: ReactTableRow<User>[] }) {
      return  [
        {
          label: 'Remove',
          onClick: () => {
            const newUsersHashMap = {...hashMap};
            rows.forEach((row) => {
              if (row.original.id in newUsersHashMap) {
                delete newUsersHashMap[row.original.id];
              }
            });
            setUserObjects(Object.values(newUsersHashMap));
            setUserIds(Object.keys(newUsersHashMap));
          },
        },
      ]
    },
    [hashMap, setUserObjects, setUserIds]
  );

  const onDoubleClickRowLeft = useCallback(
    (row: ReactTableRow<User>) => {
      // Add the user to the selection
      const newUsersHashMap = {...hashMap, [row.original.id]: row.original};
      setUserObjects(Object.values(newUsersHashMap));
      setUserIds(Object.keys(newUsersHashMap));
    },
    [hashMap, setUserObjects, setUserIds],
  );
  const onDoubleClickRowRight = useCallback(
    (row: ReactTableRow<User>) => {
      // Remove the user from the selection
      const newUsersHashMap = {...hashMap};
      if (row.original.id in hashMap) {
        delete newUsersHashMap[row.original.id];
      }
      setUserObjects(Object.values(newUsersHashMap));
      setUserIds(Object.keys(newUsersHashMap));
    },
    [hashMap, setUserObjects, setUserIds],
  );

  return (
    <Row className="text-xs user-select-none">
      <Col sm={6}>
        <div className="d-flex align-items-center" style={{ height: headerHeight }}>
          <h6 className="w-40">Find users</h6>
          <input
            className="form-control form-select-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
          />
        </div>
        {isLoading && <Loading />}
        {error && <Alert>{error}</Alert>}
        <ReactTable
          enableSearching={false}
          enablePagination={false}
          columns={columns}
          data={filteredData || []}
          enableRowSelection
          onDoubleClickRow={onDoubleClickRowLeft}
          getActions={getLeftActions}
        />
      </Col>
      <Col sm={6}>
        <div className="d-flex align-items-center justify-content-between" style={{ height: headerHeight }}>
          <h6>Selected users</h6>
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setUserObjects([]);
              setUserIds([]);
            }}
          >
            Clear
          </Button>
        </div>
        {userObjects.length > 0 ? (
          <ReactTable
            columns={columns}
            data={userObjects}
            onDoubleClickRow={onDoubleClickRowRight}
            getActions={getRightActions}
          />
        ) : (
          <NoResultsDiv>Please select a user</NoResultsDiv>
        )}
      </Col>
    </Row>
  )
}
